import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2, FileText, Building2, Clock, ShieldCheck, KeyRound } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function eur(v) {
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);
}

function dt(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("pt-PT"); } catch { return iso; }
}

export default function PublicProposal() {
  const { token } = useParams();
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Wizard: fluxo | idle | aceitar-simples | aceitar-assinar-form | otp-input | assinada
  const [flow, setFlow] = useState("idle");
  const [form, setForm] = useState({ aceite_por: "", aceite_email: "", aceite_phone: "", observacao: "" });
  const [selectedSignatarioId, setSelectedSignatarioId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(null);
  const [signSession, setSignSession] = useState(null); // {signature_token, destino_mascarado}
  const [otp, setOtp] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/public/proposals/${token}`)
      .then((r) => setProp(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Link invalido ou expirado."))
      .finally(() => setLoading(false));
  }, [token]);

  const openPdf = () => window.open(`${API}/api/public/proposals/${token}/pdf`, "_blank");

  const aceitarSimples = async () => {
    if (!form.aceite_por.trim() || !form.aceite_email.trim()) {
      return toast.error("Indique nome e email do responsavel pela aceitacao.");
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/public/proposals/${token}/aceitar`, {
        aceite_por: form.aceite_por, aceite_email: form.aceite_email, observacao: form.observacao,
      });
      setAccepted(data); setFlow("assinada");
      toast.success(`Proposta ${data.numero || ""} aceite com sucesso.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nao foi possivel aceitar.");
    } finally { setSubmitting(false); }
  };

  const iniciarAssinatura = async () => {
    if (!form.aceite_por.trim() || !form.aceite_email.trim()) {
      return toast.error("Indique nome e email.");
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/public/proposals/${token}/iniciar-assinatura`, {
        signatario_nome: form.aceite_por,
        signatario_email: form.aceite_email,
        signatario_telefone: form.aceite_phone || "",
        signatario_id: selectedSignatarioId || "",
        canais: form.aceite_phone ? ["email", "sms"] : ["email"],
      });
      setSignSession(data);
      setFlow("otp-input");
      toast.success(`Codigo enviado para ${data.destino_mascarado?.email || "-"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nao foi possivel iniciar assinatura.");
    } finally { setSubmitting(false); }
  };

  const reenviarOtp = async () => {
    if (!signSession) return;
    try {
      await axios.post(`${API}/api/sign/${signSession.signature_token}/reenviar-otp`);
      toast.success("Novo codigo enviado.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro."); }
  };

  const confirmarOtp = async () => {
    if (!otp || otp.trim().length < 4) return toast.error("Codigo invalido.");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/sign/${signSession.signature_token}/assinar`, {
        otp: otp.trim(), aceite: true,
      });
      setAccepted({ ...data, numero: prop.numero, via: "assinatura" });
      setFlow("assinada");
      toast.success("Proposta assinada.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Codigo invalido ou expirado.");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-slate-500">A carregar...</div></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center border-l-4 border-red-500">
        <div className="text-lg font-bold text-navy">Ligacao indisponivel</div>
        <div className="text-sm text-slate-600 mt-2">{error}</div>
        <div className="text-xs text-slate-400 mt-4">Contacte a equipa comercial Cogniseg.</div>
      </div>
    </div>
  );
  if (!prop) return null;

  const jaAceite = accepted || prop.aceite_em || prop.estado === "aceite" || prop.estado === "assinada";
  if (jaAceite) {
    return (
      <div className="min-h-screen bg-slate-50 py-10 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-8 border-t-4 border-emerald-500" data-testid="proposal-accepted">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-emerald-100 text-emerald-700 rounded-full p-2"><CheckCircle2 size={28} /></div>
            <div>
              <div className="text-lg font-bold text-navy">
                {accepted?.via === "assinatura" || prop.estado === "assinada" ? "Proposta assinada" : "Proposta aceite"}
              </div>
              <div className="text-xs text-slate-500">Obrigado! A equipa Cogniseg foi notificada.</div>
            </div>
          </div>
          <div className="text-sm text-slate-700 space-y-1">
            <div><b>Numero:</b> {prop.numero}</div>
            <div><b>Cliente:</b> {prop.cliente_nome || "-"}</div>
            <div><b>Total:</b> {eur(prop.total)}</div>
            {accepted?.cadeia?.order_id && <div><b>Encomenda criada:</b> {accepted.cadeia.order_id.substring(0,8)}...</div>}
            {accepted?.cadeia?.invoice_id && <div><b>Fatura emitida:</b> pendente envio por email</div>}
            {accepted?.doc_hash && <div><b>Hash do documento:</b> <span className="font-mono text-[11px]">{accepted.doc_hash.substring(0,16)}...</span></div>}
            {accepted?.assinado_em && <div><b>Assinada em:</b> {dt(accepted.assinado_em)}</div>}
          </div>
          <div className="text-xs text-slate-400 mt-6">SaaS-Hub por Cogniseg</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-white rounded-lg shadow" data-testid="public-proposal">
          <div className="bg-navy text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider opacity-70">Proposta comercial</div>
              <div className="text-xl font-bold">{prop.numero}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{eur(prop.total)}</div>
              <div className="text-xs opacity-70">{prop.moeda}</div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="text-lg font-semibold text-navy">{prop.titulo}</div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Building2 size={14} className="text-blue" /> <span>{prop.cliente_nome}{prop.cliente_nif ? ` · NIF ${prop.cliente_nif}` : ""}</span>
            </div>
            {prop.expira_em && (
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 w-fit">
                <Clock size={12} /> Valida ate {dt(prop.expira_em)}
              </div>
            )}
            <div>
              <div className="text-sm font-bold text-navy mb-2">Linhas</div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Descricao</th>
                      <th className="text-right px-3 py-2">Qtd</th>
                      <th className="text-right px-3 py-2">Preco</th>
                      <th className="text-right px-3 py-2">IVA</th>
                      <th className="text-right px-3 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(prop.itens || []).map((it, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-2">{it.descricao}</td>
                        <td className="text-right px-3 py-2">{it.quantidade}</td>
                        <td className="text-right px-3 py-2">{eur(it.preco_unitario)}</td>
                        <td className="text-right px-3 py-2">{it.iva_pct}%</td>
                        <td className="text-right px-3 py-2 font-semibold">{eur(it.total_linha)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-end">
              <div className="w-full max-w-xs text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{eur(prop.subtotal)}</span></div>
                {prop.desconto_pct_global > 0 && (
                  <div className="flex justify-between text-blue-600"><span>Desconto ({prop.desconto_pct_global}%)</span><span>-{eur(prop.desconto_global_valor)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-slate-500">IVA</span><span>{eur(prop.total_iva)}</span></div>
                <div className="flex justify-between text-lg font-bold text-navy border-t border-slate-200 pt-1"><span>Total</span><span>{eur(prop.total)}</span></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm text-slate-600 border-t border-slate-100 pt-3">
              <div><b>Condicoes pagamento:</b> {prop.condicoes_pagamento || "-"}</div>
              <div><b>Metodo pagamento:</b> {prop.metodo_pagamento || "-"}</div>
            </div>
            {prop.notas && (
              <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap">
                <div className="text-xs font-bold text-slate-500 mb-1">Notas</div>
                {prop.notas}
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={openPdf} data-testid="public-pdf-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded hover:bg-slate-50 text-sm">
                <FileText size={14} /> Descarregar PDF
              </button>
              {flow === "idle" && prop.estado !== "aceite" && prop.estado !== "assinada" && (
                <>
                  <button onClick={() => setFlow("aceitar-simples")} data-testid="public-accept-open"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-sm font-semibold">
                    <CheckCircle2 size={14} /> Aceitar proposta
                  </button>
                  <button onClick={() => setFlow("aceitar-assinar-form")} data-testid="public-sign-open"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-white rounded hover:bg-navy/90 text-sm font-semibold">
                    <ShieldCheck size={14} /> Aceitar e assinar (OTP)
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        {flow === "aceitar-simples" && (
          <div className="bg-white rounded-lg shadow p-6 space-y-3" data-testid="public-accept-form">
            <div className="text-lg font-bold text-navy flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-600" /> Confirmar aceitacao
            </div>
            <div className="text-xs text-slate-500">Ao aceitar, autoriza a criacao de encomenda e emissao de fatura conforme as condicoes acima.</div>
            <div>
              <label className="text-xs font-semibold text-navy">Nome completo</label>
              <input type="text" value={form.aceite_por} onChange={(e) => setForm({ ...form, aceite_por: e.target.value })}
                data-testid="public-nome" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">Email</label>
              <input type="email" value={form.aceite_email} onChange={(e) => setForm({ ...form, aceite_email: e.target.value })}
                data-testid="public-email" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">Observacao (opcional)</label>
              <textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                data-testid="public-observacao" rows={2} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setFlow("idle")} disabled={submitting}
                className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">Cancelar</button>
              <button onClick={aceitarSimples} disabled={submitting} data-testid="public-accept-submit"
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 font-semibold disabled:opacity-50">
                {submitting ? "A processar..." : "Confirmar aceitacao"}
              </button>
            </div>
          </div>
        )}
        {prop.signatarios && prop.signatarios.length > 0 && (
          <div className="bg-white rounded-lg shadow p-5" data-testid="public-signatarios">
            <div className="text-sm font-bold text-navy flex items-center gap-2 mb-2">
              <ShieldCheck size={16} className="text-blue-600" /> Co-signatarios ({prop.signatarios.filter((s) => s.estado === "assinou").length}/{prop.signatarios.length} assinaram)
            </div>
            <div className="space-y-1.5">
              {prop.signatarios.map((s) => (
                <div key={s.id} className={`flex items-center justify-between rounded p-2 text-sm ${s.estado === "assinou" ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50 border border-slate-200"}`}
                  data-testid={`public-sig-${s.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-navy">{s.nome}</div>
                    <div className="text-xs text-slate-500">{s.email_mascarado}</div>
                  </div>
                  <div className="text-xs">
                    {s.estado === "assinou" ? (
                      <span className="text-emerald-700 font-semibold">✓ Assinou {s.assinou_em ? new Date(s.assinou_em).toLocaleDateString("pt-PT") : ""}</span>
                    ) : (
                      <span className="text-slate-500 italic">Pendente</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {flow === "aceitar-assinar-form" && (
          <div className="bg-white rounded-lg shadow p-6 space-y-3" data-testid="public-sign-form">
            <div className="text-lg font-bold text-navy flex items-center gap-2">
              <ShieldCheck size={18} className="text-blue-600" /> Aceitar e assinar com codigo OTP
            </div>
            <div className="text-xs text-slate-500">Vai receber um codigo de 6 digitos para confirmar a assinatura da proposta. A cadeia comercial (encomenda, contrato e fatura) e gerada automaticamente apos assinatura.</div>
            {prop.signatarios && prop.signatarios.length > 0 && (
              <div>
                <label className="text-xs font-semibold text-navy">Assinar como</label>
                <select value={selectedSignatarioId} onChange={(e) => {
                  setSelectedSignatarioId(e.target.value);
                  const s = prop.signatarios.find((x) => x.id === e.target.value);
                  if (s) setForm({ ...form, aceite_por: s.nome });
                }} data-testid="sign-signatario-select"
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm bg-white">
                  <option value="">Escolha o co-signatario...</option>
                  {prop.signatarios.filter((s) => s.estado !== "assinou").map((s) => (
                    <option key={s.id} value={s.id}>{s.nome} ({s.email_mascarado})</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-navy">Nome completo</label>
              <input type="text" value={form.aceite_por} onChange={(e) => setForm({ ...form, aceite_por: e.target.value })}
                data-testid="sign-nome" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">Email (recebe OTP)</label>
              <input type="email" value={form.aceite_email} onChange={(e) => setForm({ ...form, aceite_email: e.target.value })}
                data-testid="sign-email" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">Telemovel (opcional - envia tambem por SMS)</label>
              <input type="tel" value={form.aceite_phone} onChange={(e) => setForm({ ...form, aceite_phone: e.target.value })}
                data-testid="sign-phone" placeholder="+351..." className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setFlow("idle")} disabled={submitting}
                className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">Cancelar</button>
              <button onClick={iniciarAssinatura} disabled={submitting} data-testid="sign-request-otp"
                className="px-4 py-2 text-sm bg-navy text-white rounded hover:bg-navy/90 font-semibold disabled:opacity-50">
                {submitting ? "A enviar..." : "Receber codigo OTP"}
              </button>
            </div>
          </div>
        )}
        {flow === "otp-input" && signSession && (
          <div className="bg-white rounded-lg shadow p-6 space-y-3" data-testid="public-otp-form">
            <div className="text-lg font-bold text-navy flex items-center gap-2">
              <KeyRound size={18} className="text-blue-600" /> Introduza o codigo OTP
            </div>
            <div className="text-xs text-slate-500">
              Codigo enviado para <b>{signSession.destino_mascarado?.email}</b>{signSession.destino_mascarado?.telefone ? ` e ${signSession.destino_mascarado.telefone}` : ""}. Valido durante 10 minutos.
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">Codigo (6 digitos)</label>
              <input type="text" inputMode="numeric" maxLength={6} value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                data-testid="otp-input" placeholder="000000"
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded text-2xl tracking-widest text-center font-mono" />
            </div>
            <div className="flex items-center justify-between pt-2">
              <button onClick={reenviarOtp} data-testid="otp-resend"
                className="text-xs text-blue hover:underline">Reenviar codigo</button>
              <div className="flex gap-2">
                <button onClick={() => { setFlow("aceitar-assinar-form"); setSignSession(null); setOtp(""); }} disabled={submitting}
                  className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">Voltar</button>
                <button onClick={confirmarOtp} disabled={submitting || otp.length < 4} data-testid="otp-confirm"
                  className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 font-semibold disabled:opacity-50">
                  {submitting ? "A assinar..." : "Confirmar assinatura"}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="text-center text-xs text-slate-400 pt-2">SaaS-Hub por Cogniseg · Link seguro (HMAC)</div>
      </div>
    </div>
  );
}
